/* ─────────────────────────────────────────────────────────────
   SENSITIVITY GRID (CoE × terminal g)

   The artifact's 5×5 matrix: 5 cost-of-equity rows (±1pp in 0.5pp steps) by
   5 terminal-growth columns (±1pp). The CENTRE cell equals the headline IV, so
   it is highlighted — on the stored payload that is `iv_per_share`, and on a
   scenario response it is the scenario's own IV.

   Cells are shaded relative to the centre (cheaper/richer than the headline)
   so the shape of the sensitivity reads at a glance. Null for stocks on the
   legacy fallback engine, in which case nothing renders.
───────────────────────────────────────────────────────────── */

import React from "react";
import { formatCurrency } from "../../../utils/currency";
import { decimalAsPct } from "../../../config/valuation";
import { SectionTitle, Scroller, Table, Th, Td, Tl } from "./earningsNoteUI";

/* Relative shading: green above the centre, red below, alpha by magnitude. */
const cellTint = (value, centre) => {
  if (value == null || centre == null || !centre) return undefined;
  const rel = (value - centre) / centre;
  const a = Math.min(0.22, Math.abs(rel) * 0.55);
  if (Math.abs(rel) < 0.005) return undefined;
  return rel > 0 ? `rgba(4,120,87,${a})` : `rgba(220,38,38,${a})`;
};

const SensitivityGrid = ({
  grid,
  centreValue,
  title = "Sensitivity grid",
  caption = "cost of equity × terminal growth · centre cell is the headline TV",
}) => {
  if (!Array.isArray(grid) || grid.length === 0) return null;

  // Column headers come from the first row's cells; every row shares them.
  const gCols = (grid[0]?.cells ?? [])
    .map((c) => c?.terminal_g)
    .filter((g) => g != null);
  if (gCols.length === 0) return null;

  return (
    <>
      <SectionTitle caption={caption}>{title}</SectionTitle>
      <Scroller>
        <Table>
          <thead>
            <tr>
              <Th left style={{ position: "sticky", left: 0, zIndex: 2 }}>
                CoE ↓ / terminal g →
              </Th>
              {gCols.map((g) => (
                <Th key={g}>{decimalAsPct(g, 1)}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.map((row, ri) => (
              <tr key={row?.coe ?? ri}>
                <Tl
                  style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    backgroundColor: "var(--color-surface)",
                  }}
                >
                  {decimalAsPct(row?.coe, 1)}
                </Tl>
                {(row?.cells ?? []).map((cell, ci) => {
                  const v = cell?.iv_per_share ?? null;
                  // The centre of a 5×5 grid — equal to the headline IV.
                  const isCentre =
                    ri === Math.floor(grid.length / 2) &&
                    ci === Math.floor((row.cells?.length ?? 0) / 2);
                  return (
                    <Td
                      key={cell?.terminal_g ?? ci}
                      bold={isCentre}
                      style={{
                        backgroundColor: isCentre
                          ? "var(--color-surface-hover)"
                          : cellTint(v, centreValue),
                        outline: isCentre ? "2px solid var(--color-primary)" : undefined,
                        outlineOffset: -2,
                      }}
                    >
                      {v == null ? "—" : formatCurrency(v)}
                    </Td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </Scroller>
      <div
        style={{
          marginTop: 6,
          color: "var(--color-text-muted)",
          fontSize: "var(--font-size-xs)",
          lineHeight: 1.6,
        }}
      >
        Each cell is the true value per share at that cost of equity and terminal growth.
        Shading is relative to the highlighted centre cell.
      </div>
    </>
  );
};

export default SensitivityGrid;
